import React, { useState, useContext, useEffect, useRef } from 'react';
import styles from './styles/Navbar.module.css';
import NavbarMenu from './NavbarMenu';
import AuthContext from '../../../context/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import NavbarNotifications from './NavbarNotifications';
import NavbarSearch from "./NavbarSearch"
import AuthModal from '../../auth/AuthModal';
import { FaBars, FaBell, FaHome, FaPlus, FaSearch, FaUserTie, FaShoppingBag } from 'react-icons/fa';
import CreateScenarioModal from './CreateScenarioModal';
import PermissionsContext from '../../../context/PermissionsContext';
import NotificationContext from '../../../context/NotificationContext';
import { FaChalkboardTeacher, FaUser } from 'react-icons/fa';
import useIsMobile from '../../../utils/useIsMobile';
import {
    CREATE_BASICS,
    TUTORIAL_TARGETS,
    TutorialPromptModal,
    replayableTutorials,
    tutorialAttr,
    tutorialRegistry,
    useTutorial,
} from '../../../tutorials';


const Navbar = () => {
    const { loggedIn, user, authLoading } = useContext(AuthContext);
    const { scenarioCount } = useContext(PermissionsContext);
    const { hasNewNotification, dismissBadge } = useContext(NotificationContext);

    const [showMenu, setShowMenu] = useState(false);
    const [showNotifications, setShowNotifications] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [authModalState, setAuthModalState] = useState('login');
    const [acceptInvite, setAcceptInvite] = useState(false);
    const [isSearchExpanded, setIsSearchExpanded] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showTutorialPrompt, setShowTutorialPrompt] = useState(false);
    const [createAllowed, setCreateAllowed] = useState(true);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 900);
    const navigate = useNavigate();
    const location = useLocation();

    // The guided tour teaches a drag-and-drop editor, so it is offered only where
    // dragging is how the editor works. This is the same breakpoint the tutorial
    // engine uses for its own mobile variants, not the navbar's layout one.
    const tutorialUnavailable = useIsMobile();
    const { hasBeenPrompted, markPrompted, markDismissed, start, isActive, isPaused } = useTutorial();

    /**
     * The one-time offer on the Create button is spent after the first press, so
     * the menu is where a tour is found afterwards — to see it again, or to
     * replace a practice scenario that was deleted. A replay is an ordinary run:
     * it starts at the Create dialog and builds a new scenario of its own.
     */
    const replayableTours =
        tutorialUnavailable || isActive || isPaused ? [] : replayableTutorials('create');

    const handleReplayTutorial = (tutorialId) => {
        // Someone who reaches for the tour deliberately has been offered it, so
        // the Create button need not ask them about it again.
        markPrompted(tutorialId);
        start(tutorialId);
        setShowCreateModal(true);
    };

    const notificationsWrapperRef = useRef(null);
    const menuWrapperRef = useRef(null);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 900);
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);


    useEffect(() => {
        if (location.state) {
            const { showModal, authModalState, acceptInvite } = location.state;
            setShowModal(showModal);
            setAuthModalState(authModalState);
            setAcceptInvite(acceptInvite);
        }
    }, [location.state]);

    useEffect(() => {
        if(user) setCreateAllowed((user.tier === 0 && scenarioCount < 5) || user.tier > 0);
    }, [user, scenarioCount])

    /**
     * The first time anybody presses Create — existing accounts included, since
     * the flag starts absent for everyone — they are offered the tour instead of
     * being dropped straight into the dialog.
     */
    const handleOpenCreateModal = () => {
        if (!hasBeenPrompted(CREATE_BASICS)) setShowTutorialPrompt(true);
        else setShowCreateModal(true);
    };

    const handleCloseCreateModal = () => {
        setShowCreateModal(false);
    };

    const handlePlayTutorial = () => {
        setShowTutorialPrompt(false);
        markPrompted(CREATE_BASICS);
        start(CREATE_BASICS);
        setShowCreateModal(true);
    };

    const handleSkipTutorial = () => {
        setShowTutorialPrompt(false);
        markPrompted(CREATE_BASICS);
        // Recorded separately so "never wanted it" is distinguishable from
        // "started it and left".
        markDismissed(CREATE_BASICS);
        setShowCreateModal(true);
    };

    const handleClickNotifications = () => {
        setShowNotifications(!showNotifications);
        setShowMenu(false);
        dismissBadge();
    };

    const handleClickMenu = () => {
        setShowMenu(!showMenu);
        setShowNotifications(false);
    };

    
    useEffect(() => {
        const handleClickOutsideNotifications = (event) => {
            if (notificationsWrapperRef.current && !notificationsWrapperRef.current.contains(event.target)) {
                setShowNotifications(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutsideNotifications);
        return () => {
            document.removeEventListener('mousedown', handleClickOutsideNotifications);
        };
    }, []);

    
    useEffect(() => {
        const handleClickOutsideMenu = (event) => {
            if (menuWrapperRef.current && !menuWrapperRef.current.contains(event.target)) {
                setShowMenu(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutsideMenu);
        return () => {
            document.removeEventListener('mousedown', handleClickOutsideMenu);
        };
    }, []);

    const handleClickLogIn = () => {
        setShowModal(!showModal);
        setShowNotifications(false);
        setShowMenu(false);
        setAuthModalState('login');
    }

    const handleClickSignUp = () => {
        setShowModal(!showModal);
        setShowNotifications(false);
        setShowMenu(false);
        setAuthModalState('sign up');
    }

    const toggleSearch = () => {
        setIsSearchExpanded(!isSearchExpanded);
    };


    if (authLoading) {
        return (
            <>
                { isMobile ? (
                    <div className={styles.navbarContainer}>
                        <img
                            className={styles.logo}
                            src={require('../../../images/logo_teal_background.png')}
                            alt="Home"
                            onClick={loggedIn === false ? () => navigate('/') : () => navigate('/home')}
                        />
                    </div>
                ) : (
                    <div className={styles.navbarContainer}>
                        <img
                            className={styles.logo}
                            src={require('../../../images/LOGO_aScenario.png')}
                            alt="Home"
                            onClick={loggedIn === false ? () => navigate('/') : () => navigate('/home')}
                        />
                    </div>
                )}
            </>
        );
    }

    return (
    
    <div className={styles.navbarContainer}>
        {isMobile && isSearchExpanded && (
            <div className={styles.expandedSearchContainer}>
            <NavbarSearch
                isMobile={isMobile}
                setIsSearchExpanded={setIsSearchExpanded}
            />
            </div>
        )}
        <div className={styles.left}>
            <img
            className={styles.logo}
            src={
                isMobile
                ? require('../../../images/logo_teal_background.png')
                : require('../../../images/LOGO_aScenario.png')
            }
            alt="Home"
            onClick={() => navigate(loggedIn ? '/home' : '/')}
            />
        </div>
        {/* Search and Create */}
        <div className={styles.center}>
            {!isMobile && <NavbarSearch />}

            {!isMobile && loggedIn && !acceptInvite && !user?.banned && (
            <div className={styles.tooltipWrapper}>
                <button
                className={`${styles.createNavButton} ${!createAllowed ? styles.disabled : ''}`}
                onClick={() => createAllowed && handleOpenCreateModal()}
                {...tutorialAttr(TUTORIAL_TARGETS.NAV_CREATE)}
                >
                <FaPlus className={styles.createIcon} />
                <span className={styles.createLabel}>Create</span>
                </button>

                {!createAllowed && (
                <div className={styles.tooltip}>
                    Limit reached — 5 Scenarios
                </div>
                )}
            </div>
            )}
        </div>
        <div className={styles.right}>
            {loggedIn && !acceptInvite ? (
            <>
                {isMobile && (
                <div className={styles.navbarItem} onClick={toggleSearch}>
                    <FaSearch className={styles.icon} />
                </div>
                )}

                {isMobile && !user?.banned && (
                <button
                    className={`${styles.mobileCreateButton} ${!createAllowed ? styles.disabled : ''}`}
                    onClick={() => createAllowed && handleOpenCreateModal()}
                    {...tutorialAttr(TUTORIAL_TARGETS.NAV_CREATE)}
                >
                    <FaPlus />
                </button>
                )}

                {user?.tier >= 2 && (<div className={styles.navbarItem} onClick={() => navigate('/admin')}>
                    <FaUserTie className={styles.icon} />
                    {!isMobile && <span className={styles.iconLabel}>Admin</span>}
                </div>
                )}

                {/* Notifications */}
                <div ref={notificationsWrapperRef}>
                <div className={styles.navbarItem} onClick={handleClickNotifications}>
                    <FaBell className={styles.icon} />
                    {hasNewNotification && (
                    <span className={styles.notificationBadge}>!</span>
                    )}
                </div>

                {showNotifications && (
                    <NavbarNotifications setShowNotifications={setShowNotifications} />
                )}
                </div>

                {/* Classrooms */}
                {/* <button
                className={styles.navbarButton}
                onClick={() => navigate('/my-classrooms')}
                >
                <FaChalkboardTeacher className={styles.icon} />
                </button> */}

                {/* Account */}
                <button
                className={styles.navbarButton}
                onClick={() => navigate(`/user/${user.username}`)}
                >
                {user?.profilePicture ? (
                    <img
                    src={user.profilePicture}
                    alt="Profile"
                    className={styles.profilePicture}
                    />
                ) : (
                    <FaUser className={styles.icon} />
                    
                )}
                {/* {!isMobile && <span className={styles.iconLabel}>{user?.username}</span>} */}
                </button>

                {/* Menu */}
                <div ref={menuWrapperRef}>
                <div className={styles.navbarItem} onClick={handleClickMenu}>
                    <FaBars className={styles.icon} />
                </div>
                {showMenu && (
                    <NavbarMenu
                        setShowMenu={setShowMenu}
                        user={user}
                        tutorials={replayableTours}
                        onReplayTutorial={handleReplayTutorial}
                        replayBlocked={!createAllowed}
                    />
                )}
                
                </div>
            </>
            ) : (
            !authLoading && (
                <>
                {isMobile && (
                    <div className={styles.navbarItem} onClick={toggleSearch}>
                    <FaSearch className={styles.icon} />
                    </div>
                )}
                <button className={styles.login} onClick={handleClickLogIn}>
                    Log In
                </button>
                <button className={styles.signup} onClick={handleClickSignUp}>
                    Sign Up
                </button>
                </>
            )
            )}
        </div>

        {/* MODALS */}
        {showModal && (
            <AuthModal
            showModal={showModal}
            setShowModal={setShowModal}
            authModalState={authModalState}
            setAuthModalState={setAuthModalState}
            acceptInvite={acceptInvite}
            setAcceptInvite={setAcceptInvite}
            />
        )}

        {showTutorialPrompt && (
            <TutorialPromptModal
                content={tutorialRegistry[CREATE_BASICS].content.prompt}
                isMobile={tutorialUnavailable}
                onPlay={handlePlayTutorial}
                onSkip={handleSkipTutorial}
            />
        )}

        <CreateScenarioModal
            isOpen={showCreateModal}
            onClose={handleCloseCreateModal}
        />
        </div>


    
    
    );
    
    
};

export default Navbar;